import React, {
  useCallback, useContext, useEffect
} from 'react';
import { Link } from 'react-router-dom';

import { APIClient } from '../client';
import { AuthUserContext, ScreenHeader } from '../components';
import { PeopleProvider } from '../context';
import { objectToQueryString } from '../hooks';

import './dashboard-components-styles.scss';
import { demoArticles } from './wellness/demo-articles';
import { demoVideos } from './wellness/demo-videos';


interface ProgressSectionProps {
  section: {
    title: string;
    type: string;
    percentage: number;
  };
}

interface Document {
  id: string;
  name: string;
  location: string;
  user_id: string;
  created_datetime: string;
  modified_datetime: string;
}

interface ApiBeneficiary {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

interface ApiAsset {
  id: string;
  name: string;
  assetType: string;
  description: string;
  assetValue?: number;
  willId: string;
  beneficiaries: ApiBeneficiary[];
}

interface ApiExecutor {
  id: string;
  isPrimary: boolean;
  personId: string;
  willId: string;
  person: {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    contactEmail?: string;
    contactPhone?: string;
  };
}

interface WillDocument extends Document {
  status: string;
  personId: string;
  primaryBeneficiaryId: string;
  assets: ApiAsset[];
  executors: ApiExecutor[];
  arrangements: unknown[];
}

interface WellnessItem {
  id: string;
  title: string;
  description: string;
  meta: string;
  icon: string;
  url: string;
}

const getStatusColorClass = (percent: number) => {
  switch (percent) {
    case 100: return 'status-complete';
    case 0: return 'status-not-started';
    default: return 'status-started';
  }
};

const getPercentComplete = (document: Document | WillDocument | null) => {
  if (!document) {
    return 0;
  }

  return 50;
};

const getContinueSettings = (type: string, document: Document | WillDocument | null) => {
  if (!document) {
    return {
      text: 'Start',
      href: `/people/self/life-documents/${type}/new`
    };
  }
  return {
    text: 'Continue',
    href: `/people/self/life-documents/${type}/new/continue`
  };
};

const getStatusText = (percentage: number) => {
  switch (percentage) {
    case 100: return 'Complete';
    case 0: return 'Not Started';
    default: return 'Started';
  }
};

function ProgressSection({ section }: ProgressSectionProps) {
  const [ expanded, setExpanded ] = React.useState(false);
  const [ document, setDocument ] = React.useState<Document | WillDocument | null>(null);

  useEffect(() => {
    const getDocument = async () => {
      const query = { document: section.type };
      const queryString = objectToQueryString(query);
      const client = new APIClient(`api/documents?${queryString}`);

      try {
        const document = await client.index();
        setDocument(document);
      } catch (error) {
        console.error('Error fetching document', error);
        setDocument(null);
      }      
    };

    if (section.type) {
      getDocument();
    }
  }, [ section.type ]);

  const handleExpand = useCallback(() => {
    setExpanded(!expanded);
  }, [ expanded ]);

  const percentage = getPercentComplete(document);

  const status = getStatusColorClass(percentage);

  const statusText = getStatusText(percentage);

  const continueSettings = getContinueSettings(section.type, document);

  return (
    <div className="section progress-section">
      <div className="progress-section-header">
        <h4 className="progress-section-title">
          {section.title}
        </h4>
        <div className="progress-section-right">
          <PercentBar percentage={percentage} status={status} />
        </div>
      </div>
      <div className="progress-status-row">
        <span className={'progress-status-text'}>
          {statusText}
        </span>
        <Link to={continueSettings.href} style={{ textDecoration: 'none' }}>
          <span className="progress-continue-text">
            {continueSettings.text}
          </span>
        </Link>
      </div>
      
      {expanded && (
        <ExpandedSection type={section.type} document={document} />
      )}

      {(status === 'status-started' || status === 'status-complete') && (
        <div className="progress-expand-container">
          <button
            className="expand-button"
            onClick={handleExpand}
          >
            <svg 
              className={`expand-icon ${expanded ? 'expanded' : ''}`}
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function PercentBar({ percentage, status }: { percentage: number, status: string }) {
  const getProgressBarColorClass = (status: string) => {
    switch (status) {
      case 'status-complete': return 'progress-complete';
      case 'status-started': return 'progress-started';
      case 'status-not-started': return 'progress-not-started';
      default: return 'progress-not-started';
    }
  };

  return (
    <div className="progress-bar-wrapper">
      <div className="progress-bar">
        <div 
          className={`progress-fill ${getProgressBarColorClass(status)}`}
          style={{ width: `${percentage}%` }}
        ></div>
        <div className="progress-percentage">{percentage}%</div>
      </div>
    </div>
  );
}

const progressData = [
  { 
    title: 'Will & Testament', 
    type: 'will',
    percentage: 60
  },
  { 
    title: 'Other Bequests', 
    type: 'other-bequests',
    percentage: 0
  },
  { 
    title: 'Healthcare Directive', 
    type: 'healthcare-directive',
    percentage: 100
  },
  { 
    title: 'Funeral Arrangements', 
    type: 'funeral-arrangements',
    percentage: 0
  },
  { 
    title: 'Power of Attorney', 
    type: 'power-of-attorney',
    percentage: 40
  },
  { 
    title: 'Letter to Survivors', 
    percentage: 0,
    type: 'letter-to-survivors'
  }
];

// Will Section Component
function WillSection({ document }: { document: WillDocument | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No will document found</div>
      </div>
    );
  }

  // Count unique beneficiaries across all assets
  const allBeneficiaries = document.assets.flatMap(asset => asset.beneficiaries || []);
  const uniqueBeneficiaries = allBeneficiaries.filter((beneficiary, index, self) => 
    index === self.findIndex(b => b.id === beneficiary.id)
  );

  return (
    <div className="details-container">
      <ul className="details-list">
        <li className="detail-item">
          <div className="detail-bullet"></div>
          {document.assets.length} asset{document.assets.length !== 1 ? 's' : ''} added
        </li>
        <li className="detail-item">
          <div className="detail-bullet"></div>
          {document.executors.length} executor{document.executors.length !== 1 ? 's' : ''} assigned
        </li>
        <li className="detail-item">
          <div className="detail-bullet"></div>
          {uniqueBeneficiaries.length} beneficiar{uniqueBeneficiaries.length !== 1 ? 'ies' : 'y'} named
        </li>
      </ul>
    </div>
  );
}

// Placeholder sections for other document types
function OtherBequestsSection({ document }: { document: Document | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No document found</div>
      </div>
    );
  }

  return (
    <div className="details-container">
      <div className="document-in-progress-text">Document in progress</div>
    </div>
  );
}

function HealthcareDirectiveSection({ document }: { document: Document | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No document found</div>
      </div>
    );
  }

  return (
    <div className="details-container">
      <div className="document-in-progress-text">Document in progress</div>
    </div>
  );
}

function FuneralArrangementsSection({ document }: { document: Document | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No document found</div>
      </div>
    );
  }

  return (
    <div className="details-container">
      <div className="document-in-progress-text">Document in progress</div>
    </div>
  );
}

function PowerOfAttorneySection({ document }: { document: Document | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No document found</div>
      </div>
    );
  }

  return (
    <div className="details-container">
      <div className="document-in-progress-text">Document in progress</div>
    </div>
  );
}

function LetterToSurvivorsSection({ document }: { document: Document | null }) {
  if (!document) {
    return (
      <div className="details-container">
        <div className="no-document-text">No document found</div>
      </div>
    );
  }

  return (
    <div className="details-container">
      <div className="document-in-progress-text">Document in progress</div>
    </div>
  );
}

// ExpandedSection component that routes to specific section components
function ExpandedSection({ type, document }: { type: string, document: Document | WillDocument | null }) {
  switch (type) {
    case 'will':
      return <WillSection document={document as WillDocument | null} />;
    case 'other-bequests':
      return <OtherBequestsSection document={document} />;
    case 'healthcare-directive':
      return <HealthcareDirectiveSection document={document} />;
    case 'funeral-arrangements':
      return <FuneralArrangementsSection document={document} />;
    case 'power-of-attorney':
      return <PowerOfAttorneySection document={document} />;
    case 'letter-to-survivors':
      return <LetterToSurvivorsSection document={document} />;
    default:
      return (
        <div className="details-container">
          <div className="no-document-text">Unknown document type</div>
        </div>
      );
  }
}

// Enhanced Document Progress Section Component
const DocumentProgressSection = () => {
  const [ showAllSections, setShowAllSections ] = React.useState(false);

  const handleToggleShowAllSections = useCallback(() => {
    setShowAllSections(!showAllSections);
  }, [ showAllSections ]);

  const sectionsToShow = showAllSections ? progressData : progressData.slice(0, 3);

  return (
    <div className="section">
      <div className="section-header">
        <svg className="section-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
        </svg>
        <div className="section-title">Your Progress</div>
      </div>

      {
        sectionsToShow.map((item, index) => (
          <ProgressSection key={index} section={item} />
        ))
      }
      
      {progressData.length > 3 && (
        <button
          className="show-more-button"
          onClick={handleToggleShowAllSections}
        >
          <span>{showAllSections ? 'Show less' : 'Show more'}</span>
        </button>
      )}
    </div>
  );
};

// Recommended Actions Section Component
const RecommendedActionsSection = () => {
  const [ hoveredAction, setHoveredAction ] = React.useState<number | null>(null);

  const actionItems = [
    {
      title: 'Add More Assets',
      description: 'Complete your asset inventory to get personalized recommendations',
      icon: 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z'
    },
    {
      title: 'Record Your Wishes',
      description: 'Start documenting your preferences for healthcare and finances',
      icon: 'M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z'
    },
    {
      title: 'Review Beneficiaries',
      description: 'Ensure your asset beneficiaries are up to date',
      icon: 'M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z'
    }
  ];

  const handleMouseEnter = React.useCallback((index: number) => {
    setHoveredAction(index);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setHoveredAction(null);
  }, []);

  const createMouseEnterHandler = React.useCallback((index: number) => {
    return () => handleMouseEnter(index);
  }, [ handleMouseEnter ]);

  return (
    <div className="section">
      <div className="section-header">
        <svg className="section-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17M11,9H13V7H11V9Z"/>
        </svg>
        <div className="section-title">Recommended Next Steps</div>
      </div>

      {actionItems.map((item, index) => (
        <button
          key={index}
          className={`action-item ${hoveredAction === index ? 'hovered' : ''} ${index === actionItems.length - 1 ? 'last-item' : ''}`}
          onMouseEnter={createMouseEnterHandler(index)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="action-icon">
            <svg className="action-icon-svg" viewBox="0 0 24 24" fill="currentColor">
              <path d={item.icon}/>
            </svg>
          </div>
          <div className="action-content">
            <h4 className="action-title">{item.title}</h4>
            <p className="action-description">{item.description}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

// Transform demo data into wellness items
const transformVideosToWellnessItems = (videos: typeof demoVideos): WellnessItem[] => {
  return videos.map(video => ({
    id: video.id,
    title: video.title,
    description: video.description,
    meta: `Video • ${video.duration}`,
    icon: 'M8,5.14V19.14L19,12.14L8,5.14Z', // Play icon for videos
    url: `/wellness/videos/${video.id}`
  }));
};

const transformArticlesToWellnessItems = (articles: typeof demoArticles): WellnessItem[] => {
  return articles.map(article => ({
    id: article.id,
    title: article.title,
    description: article.summary,
    meta: 'Article • 5 min read', // Default reading time
    icon: 'M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M17,17H7V15H17V17M17,13H7V11H17V13M17,9H7V7H17V9Z', // Article icon
    url: `/wellness/articles/${article.id}`
  }));
};

const wellnessContent = {
  videos: transformVideosToWellnessItems(demoVideos),
  articles: transformArticlesToWellnessItems(demoArticles)
};

// Wellness Section Component
const WellnessSection = () => {
  const [ hoveredWellness, setHoveredWellness ] = React.useState<number | null>(null);
  const [ selectedItems, setSelectedItems ] = React.useState<WellnessItem[]>([]);

  // Select one random video and one random article on component mount
  React.useEffect(() => {
    const randomVideo = wellnessContent.videos[Math.floor(Math.random() * wellnessContent.videos.length)];
    const randomArticle = wellnessContent.articles[Math.floor(Math.random() * wellnessContent.articles.length)];
    setSelectedItems([ randomVideo, randomArticle ]);
  }, []);

  const handleWellnessMouseEnter = React.useCallback((index: number) => {
    setHoveredWellness(index);
  }, []);

  const handleWellnessMouseLeave = React.useCallback(() => {
    setHoveredWellness(null);
  }, []);

  const createWellnessMouseEnterHandler = React.useCallback((index: number) => {
    return () => handleWellnessMouseEnter(index);
  }, [ handleWellnessMouseEnter ]);


  return (
    <div className="section">
      <div className="section-header">
        <svg className="section-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.1,18.55L12,18.65L11.89,18.55C7.14,14.24 4,11.39 4,8.5C4,6.5 5.5,5 7.5,5C9.04,5 10.54,6 11.07,7.36H12.93C13.46,6 14.96,5 16.5,5C18.5,5 20,6.5 20,8.5C20,11.39 16.86,14.24 12.1,18.55M16.5,3C14.76,3 13.09,3.81 12,5.08C10.91,3.81 9.24,3 7.5,3C4.42,3 2,5.41 2,8.5C2,12.27 5.4,15.36 10.55,20.03L12,21.35L13.45,20.03C18.6,15.36 22,12.27 22,8.5C22,5.41 19.58,3 16.5,3Z"/>
        </svg>
        <div className="section-title">Wellness for You</div>
      </div>

      {selectedItems.map((item, index) => (
        <Link
          key={index}
          to={item.url}
          className={`wellness-item ${hoveredWellness === index ? 'hovered' : ''} ${index === selectedItems.length - 1 ? 'last-item' : ''}`}
          onMouseEnter={createWellnessMouseEnterHandler(index)}
          onMouseLeave={handleWellnessMouseLeave}
        >
          <div className="wellness-icon">
            <svg className="wellness-icon-svg" viewBox="0 0 24 24" fill="currentColor">
              <path d={item.icon}/>
            </svg>
          </div>
          <div className="wellness-content">
            <h4 className="wellness-title">{item.title}</h4>
            <p className="wellness-description">{item.description}</p>
            <div className="wellness-meta">{item.meta}</div>
          </div>
        </Link>
      ))}
    </div>
  );
};

const DashboardContent = () => {
  const { user } = useContext(AuthUserContext);
  return (
    <PeopleProvider query={{ linked_user_id: user?.id }}> 
      <div className="dashboard-component screen dashboard-container">
        <ScreenHeader
          title="Your most important journey"
          description="Welcome back to your dashboard"
        />
        <DocumentProgressSection />
        <RecommendedActionsSection />
        <WellnessSection />
        <div className="bottom-spacer"></div>
      </div>
    </PeopleProvider>
  );
};

export default DashboardContent;